import React from 'react';
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    Paper,
    Tooltip,
    IconButton
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import useInvoiceActivityLog from '../../../hooks/purchaseInvoices/invoiceManagement/useInvoiceActivityLog';

const LEVEL_CONFIG = {
    ERROR: {
        color: '#d32f2f',
        bgColor: '#fff5f5',
        borderColor: '#ffcdd2',
        icon: <ErrorOutlineIcon sx={{ color: '#d32f2f', fontSize: 20 }} />,
        label: 'שגיאה',
        chipColor: 'error'
    },
    WARNING: {
        color: '#e65100',
        bgColor: '#fff8f0',
        borderColor: '#ffe0b2',
        icon: <WarningAmberIcon sx={{ color: '#e65100', fontSize: 20 }} />,
        label: 'אזהרה',
        chipColor: 'warning'
    },
    SUCCESS: {
        color: '#1b5e20',
        bgColor: '#f0fdf4',
        borderColor: '#c8e6c9',
        icon: <CheckCircleOutlineIcon sx={{ color: '#2e7d32', fontSize: 20 }} />,
        label: 'הצלחה',
        chipColor: 'success'
    }
};

const STAGE_LABELS = {
    DEBIT_NOTE_VALIDATION: 'ולידציית חשבון חיוב',
    TRANSACTION_INVOICE_VALIDATION: 'ולידציית חשבונית עסקה',
    COMPANY_ID_VALIDATION: 'אימות ח.פ חברה',
    DUPLICATE_CHECK: 'בדיקת כפילויות',
    PRIORITY_CREATION_FAILED: 'קליטה בפריוריטי נכשלה',
    PRIORITY_CREATION: 'קליטה בפריוריטי',
    PRIORITY_RETRY_STARTED: 'ניסיון חוזר לפריוריטי',
    PRIORITY_RETRY_VALIDATION: 'ולידציית ניסיון חוזר',
    PRIORITY_RETRY_LOCKED: 'תהליך נעול',
    BUDGET_VALIDATION: 'ולידציית תקציב',
    ALLOCATION: 'שיוך הקצאות',
    ALLOCATION_ERROR: 'שגיאת שיוך',
    APPROVAL_COMPLETE: 'אישור הושלם',
    EMAIL_IMPORT_ERROR: 'שגיאת ייבוא מייל',
    ERROR_EMAIL_SENT: 'מייל שגיאה נשלח',
    ERROR_EMAIL_FAILED: 'שליחת מייל שגיאה נכשלה',
    ERROR_EMAIL_MISSING: 'מייל שולח חסר',
    ACCOUNTANT_FOLLOWUP_EMAIL: 'מייל תזכורת לרואת חשבון',
    GOODS_RECEIPT_ALERT: 'התראת קבלת סחורה',
    VALIDATION_START: 'תחילת ולידציה',
    VALIDATION_COMPLETE: 'ולידציה הושלמה',
    APPROVAL: 'אישור',
    EXCHANGE_RATE: 'שע"ח',
    PRICE_UPDATE: 'עדכון מחיר'
};

const formatDate = (dateString) => {
    try {
        const date = new Date(dateString);
        return date.toLocaleString('he-IL', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric'
        });
    } catch {
        return dateString;
    }
};

const ActivityItem = ({ activity }) => {
    const config = LEVEL_CONFIG[activity.level] || LEVEL_CONFIG.ERROR;
    const stageLabel = STAGE_LABELS[activity.stage] || activity.stage;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 2,
                mb: 1.5,
                borderRadius: '10px',
                border: `1px solid ${config.borderColor}`,
                backgroundColor: config.bgColor,
                display: 'flex',
                gap: 1.5,
                alignItems: 'flex-start'
            }}
        >
            <Box sx={{ mt: 0.2, flexShrink: 0 }}>{config.icon}</Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                    <Chip
                        label={config.label}
                        color={config.chipColor}
                        size="small"
                        sx={{ fontWeight: 700, fontSize: '0.7rem', height: 20 }}
                    />
                    <Typography
                        variant="caption"
                        sx={{
                            fontWeight: 600,
                            color: '#555',
                            background: '#f0f0f0',
                            px: 1,
                            py: 0.3,
                            borderRadius: '6px',
                            fontSize: '0.7rem'
                        }}
                    >
                        {stageLabel}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#999', mr: 'auto', fontSize: '0.7rem' }}>
                        {formatDate(activity.timestamp)}
                    </Typography>
                </Box>
                <Typography
                    variant="body2"
                    sx={{ color: config.color, fontWeight: 600, mb: activity.details && Object.keys(activity.details).length > 0 ? 0.5 : 0 }}
                    dir="rtl"
                >
                    {activity.message}
                </Typography>
                {activity.details && Object.keys(activity.details).length > 0 && (
                    <Box
                        sx={{
                            mt: 0.5,
                            fontSize: '0.72rem',
                            color: '#666',
                            fontFamily: 'monospace',
                            background: 'rgba(0,0,0,0.04)',
                            p: 0.8,
                            borderRadius: '6px',
                            maxHeight: 80,
                            overflowY: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all'
                        }}
                    >
                        {JSON.stringify(activity.details, null, 2)}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

const InvoiceActivityLogTab = ({ invoice }) => {
    const { activities, isLoading, error, refetch } = useInvoiceActivityLog(invoice);

    const errorCount = activities.filter(a => a.level === 'ERROR').length;
    const warningCount = activities.filter(a => a.level === 'WARNING').length;
    const successCount = activities.filter(a => a.level === 'SUCCESS').length;

    return (
        <Box sx={{ p: 3 }} dir="rtl">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, color: '#1976d2', display: 'flex', alignItems: 'center', gap: 1 }}
                >
                    יומן פעילות חשבונית
                </Typography>
                <Tooltip title="רענן">
                    <IconButton onClick={refetch} size="small">
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            {!isLoading && !error && activities.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {errorCount > 0 && (
                        <Chip icon={<ErrorOutlineIcon />} label={`${errorCount} שגיאות`} color="error" size="small" variant="outlined" />
                    )}
                    {warningCount > 0 && (
                        <Chip icon={<WarningAmberIcon />} label={`${warningCount} אזהרות`} color="warning" size="small" variant="outlined" />
                    )}
                    {successCount > 0 && (
                        <Chip icon={<CheckCircleOutlineIcon />} label={`${successCount} הצלחות`} color="success" size="small" variant="outlined" />
                    )}
                </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            {isLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!isLoading && !error && activities.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 5, color: '#aaa' }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 40, mb: 1, color: '#c8e6c9' }} />
                    <Typography variant="body2">
                        לא נמצאו אירועים חשובים ביומן הפעילות
                    </Typography>
                </Box>
            )}

            {!isLoading && activities.map((activity, index) => (
                <ActivityItem key={index} activity={activity} />
            ))}
        </Box>
    );
};

export default InvoiceActivityLogTab;
